import React, { useEffect, useState } from 'react';
import { CheckCircle, Headset, Circle, Info } from '@phosphor-icons/react';
import Modal from '../../components/molecules/Modal/Modal';
import {
	ACCOUNT_SUPPORT_TYPES,
	CONSULTATION_TYPES,
	FISCAL_INQUIRY_TYPES,
	MESSAGE_TEMPLATES,
	SERVICE_TYPES,
} from './constants';
import {
	AccountSupportType,
	FormData,
	ServiceType,
	FiscalInquiryType,
} from './types';
import { ConsultationType } from './types';
import PrimaryButton from '../../components/atoms/Button';
import ROUTES from '../../shared/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { sendEscalationForm } from '../../services/heru-ai.service';
import { showAlert } from '../../redux/slices/alert.slice';
import { ALERT_TYPE } from '../../components/molecules/Alert';
import { EVENT_TYPES } from '../../shared/constants/events';
import usePageView from '../../hooks/usePageView';

const SupportFormPage = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { user: userProfile } = useSelector((state: RootState) => state.profile);
	const tokenSession = useSelector(
		(state: RootState) => state.auth.tokenSession,
	);
	const [formData, setFormData] = useState<FormData>({
		email: userProfile?.email || '',
		consultationType: '',
		message: '',
	});
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const { trackPageView } = usePageView({
		sectionVisit: EVENT_TYPES.PAGE_VIEW_SUPPORT_FORM
	});

	const handleConsultationTypeChange = (type: ConsultationType) => {
		setFormData((prev: FormData) => ({
			...prev,
			consultationType: type,
			accountSupportType: undefined,
			selectedServices: undefined,
			fiscalInquiryType: undefined,
			message: '',
		}));
	};

	const handleAccountSupportTypeChange = (type: AccountSupportType) => {
		setFormData((prev: FormData) => ({
			...prev,
			accountSupportType: type,
			message: MESSAGE_TEMPLATES[type],
		}));
	};

	const handleServiceTypesChange = (service: ServiceType) => {
		setFormData((prev: FormData) => ({
			...prev,
			selectedServices: [service],
			message: MESSAGE_TEMPLATES[service],
		}));
	};

	const handleFiscalInquiryTypeChange = (type: FiscalInquiryType) => {
		setFormData((prev: FormData) => ({
			...prev,
			fiscalInquiryType: type,
			message: MESSAGE_TEMPLATES[type],
		}));
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		submitSupportForm();
	};

	const submitSupportForm = async () => {
		setIsLoading(true);
		const subjectType: Record<ConsultationType, string> = {
			'service-inquiry': 'Consulta sobre servicios',
			'account-support': 'Soporte',
			'fiscal-inquiry': 'Asesoría fiscal',
		};
		try {
			const date = new Date()
				.toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })
				.split(',')[0];
			const time = new Date()
				.toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })
				.split(',')[1];
			const body = {
				subject: `Escalación - ${subjectType[formData.consultationType as ConsultationType]} - ${userProfile?.email} - ${date} ${time}`,
				category_type: formData.consultationType,
				category_detail:
					formData.fiscalInquiryType ||
					formData.accountSupportType ||
					formData.selectedServices?.join(', '),
				html_content: `
            <b>Información de contacto:</b>
            <ul>
                <li>Nombre: ${userProfile?.first_name} ${userProfile?.father_surname}</li>
                <li>Email: ${userProfile?.email}</li>
                <li>Teléfono: ${userProfile?.cellphone}</li>
                <li>Fecha: ${date} - ${time}</li>
            </ul>
			
			<p style="white-space: pre-wrap">${formData.message}</p>`,
				type:
					formData.consultationType === 'service-inquiry' ? 'sales' : 'support',
			};
			await sendEscalationForm(body, tokenSession);
			setShowConfirmation(true);
		} catch (error) {
			const alert = {
				type: ALERT_TYPE.ERROR,
				title: 'Error al enviar la solicitud',
				description:
					'Por favor, intenta nuevamente, si el problema persiste, contacta al soporte al correo ayuda@heru-app.com',
			};
			dispatch(showAlert(alert));
		} finally {
			setIsLoading(false);
		}
	};

	const handleCloseModal = () => {
		setShowConfirmation(false);
		navigate(ROUTES.ROOT);
	};

	const minMessageLength = formData?.message?.length < 30;

	const messageIsDefault =
		(formData?.accountSupportType &&
			(formData?.message === MESSAGE_TEMPLATES[formData?.accountSupportType] ||
				formData?.message.length <
					MESSAGE_TEMPLATES[formData?.accountSupportType].length + 50)) ||
		(formData?.fiscalInquiryType &&
			(formData?.message === MESSAGE_TEMPLATES[formData?.fiscalInquiryType] ||
				formData?.message.length <
					MESSAGE_TEMPLATES[formData?.fiscalInquiryType].length + 50)) ||
		(formData?.selectedServices &&
			formData?.selectedServices?.length > 0 &&
			(formData?.message === MESSAGE_TEMPLATES[formData?.selectedServices[0]] ||
				formData?.message.length <
					MESSAGE_TEMPLATES[formData?.selectedServices[0]].length + 50));

	const isButtonDisabled =
		formData?.consultationType === '' ||
		formData?.message?.length === 0 ||
		messageIsDefault ||
		minMessageLength;

	const showInputMessage =
		formData?.consultationType &&
		formData?.message &&
		formData?.message?.length > 0;

	useEffect(() => {
		trackPageView();
	}, []);

	useEffect(() => {
		if (userProfile?.email) {
			setFormData((prev: FormData) => ({
				...prev,
				email: userProfile?.email || '',
			}));
		}
	}, [userProfile?.email]);

	return (
		<section className='text-sm bg-gradient-to-b from-primary-50 to-white py-2 md:py-6 px-3 fade-in-scale'>
			<div className='max-w-2xl mx-auto flex flex-col gap-4'>
				<div className='bg-gradient-to-tr from-h-primary-90/80 to-h-primary-90/20 rounded-lg border border-h-primary-100 p-4 shadow-sm text-h-dark flex flex-col gap-1 md:gap-2'>
					<h2 className='text-base md:text-xl font-bold tracking-tight opacity-80'>
						¿Cómo podemos asistirte hoy?
					</h2>
					<div className='flex items-end justify-between gap-4'>
						<p className='max-w-[500px] text-sm md:text-[15px] leading-tight opacity-80'>
							Tu tranquilidad es nuestra prioridad. Nuestro equipo de expertos
							analizará tu caso y te responderá en el menor tiempo posible.
						</p>
						<Headset
							size={32}
							weight='duotone'
							className='min-w-8 min-h-8 text-h-primary'
						/>
					</div>
				</div>

				<div className='bg-white rounded-2xl shadow-soft p-5 md:p-8'>
					<form
						onSubmit={handleSubmit}
						className='space-y-8'
					>
						<div>
							<label
								htmlFor='email'
								className='block text-sm font-medium text-gray-700 mb-2'
							>
								Tu email
							</label>
							<input
								type='email'
								id='email'
								value={formData.email}
								onChange={(e) =>
									setFormData((prev: FormData) => ({
										...prev,
										email: e.target.value,
									}))
								}
								className='block w-full rounded-lg border-h-neutral-90 border bg-white p-2 outline-none text-h-neutral-40'
								required
								readOnly
							/>
							<p className='text-xs text-gray-500 mt-1'>
								Enviaremos una copia de tu solicitud a tu correo. Si quieres
								modificarlo{' '}
								<button
									type='button'
									onClick={() => {
										navigate(ROUTES.ACCOUNT_HERU);
									}}
									className='font-bold'
								>
									haz clic aquí
								</button>
								.
							</p>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-2'>
								Tipo de consulta
							</label>
							<div className='mt-1 flex flex-col gap-2'>
								{Object.entries(CONSULTATION_TYPES).map(([value, label]) => (
									<div
										key={value}
										className='relative flex items-center'
									>
										<div className='flex items-center h-5 text-h-primary'>
											<Circle
												size={20}
												weight={
													formData.consultationType === value ? 'fill' : 'bold'
												}
												className='text-primary-600 cursor-pointer'
												onClick={() =>
													handleConsultationTypeChange(
														value as ConsultationType,
													)
												}
											/>
										</div>
										<label
											htmlFor={value}
											className='ml-3 block text-sm text-gray-700 cursor-pointer'
											onClick={() =>
												handleConsultationTypeChange(value as ConsultationType)
											}
										>
											{label}
										</label>
									</div>
								))}
							</div>
						</div>

						{formData.consultationType === 'fiscal-inquiry' && (
							<div className='animate-fade-in'>
								<label className='block text-sm font-medium text-gray-700 mb-2'>
									¿Qué tipo de asesoría fiscal requieres?
								</label>
								<select
									title='¿Qué tipo de asesoría fiscal requieres?'
									className='mt-1 block w-full rounded-lg border-gray-300 bg-h-neutral-94 p-1 outline-none text-sm'
									value={formData.fiscalInquiryType || ''}
									onChange={(e) =>
										handleFiscalInquiryTypeChange(
											e.target.value as FiscalInquiryType,
										)
									}
									required
								>
									<option value=''>Selecciona una opción</option>
									{Object.entries(FISCAL_INQUIRY_TYPES).map(
										([value, label]) => (
											<option
												key={value}
												value={value}
											>
												{label}
											</option>
										),
									)}
								</select>
							</div>
						)}

						{formData.consultationType === 'account-support' && (
							<div className='animate-fade-in'>
								<label className='block text-sm font-medium text-gray-700 mb-2'>
									¿Qué tipo de soporte requieres?
								</label>
								<select
									title='¿Qué tipo de soporte requieres?'
									className='mt-1 block w-full rounded-lg border-gray-300 bg-h-neutral-94 p-1 outline-none text-sm'
									value={formData.accountSupportType || ''}
									onChange={(e) =>
										handleAccountSupportTypeChange(
											e.target.value as AccountSupportType,
										)
									}
									required
								>
									<option value=''>Selecciona una opción</option>
									{Object.entries(ACCOUNT_SUPPORT_TYPES).map(
										([value, label]) => (
											<option
												key={value}
												value={value}
											>
												{label}
											</option>
										),
									)}
								</select>
							</div>
						)}

						{formData.consultationType === 'service-inquiry' && (
							<div className='animate-fade-in'>
								<label className='block text-sm font-medium text-gray-700 mb-2'>
									¿Qué tipo de servicio quieres contratar?
								</label>
								<div className='mt-2 space-y-3'>
									{Object.entries(SERVICE_TYPES).map(([value, label]) => {
										const isSelected = formData.selectedServices?.includes(
											value as ServiceType,
										);
										return (
											<button
												type='button'
												onClick={() =>
													handleServiceTypesChange(value as ServiceType)
												}
												key={value}
												className='relative flex items-center'
											>
												<div className='flex items-center h-5 text-h-primary'>
													{isSelected ? (
														<Circle
															size={20}
															weight='fill'
															className='text-primary-600 cursor-pointer'
														/>
													) : (
														<Circle
															size={20}
															weight='bold'
															className='text-primary-600 cursor-pointer'
														/>
													)}
												</div>
												<label
													htmlFor={value}
													className='ml-3 block text-gray-700 text-left'
												>
													{label}
												</label>
											</button>
										);
									})}
								</div>
							</div>
						)}

						{showInputMessage && (
							<div className='animate-fade-in'>
								<label
									htmlFor='message'
									className='block text-sm font-medium text-gray-700 mb-'
								>
									Consulta
								</label>
								<p className='text-sm text-gray-500 mb-2'>
									Hemos precargado tu solicitud, por favor complementa con la
									información requerida
								</p>
								<textarea
									id='message'
									rows={15}
									value={formData.message}
									onChange={(e) =>
										setFormData((prev: FormData) => ({
											...prev,
											message: e.target.value,
										}))
									}
									className='block w-full rounded-lg border-gray-300 border !bg-white p-2 outline-none text-base md:text-sm'
									required
								/>
							</div>
						)}

						<div className='flex flex-col gap-2'>
							{(messageIsDefault || minMessageLength) && showInputMessage && (
								<div className='bg-h-dark/5 rounded-lg p-2 flex items-center gap-2 text-h-dark '>
									<Info
										weight='duotone'
										size={20}
										className='min-w-5 min-h-5'
									/>
									<p className='text-sm leading-tight'>
										<b>Por favor agrega más detalle a tu solicitud </b> para que
										podamos ayudarte mejor y así responderte en el menor tiempo
										posible.
									</p>
								</div>
							)}

							<PrimaryButton
								label='Enviar solicitud'
								onClick={handleSubmit}
								disabled={isButtonDisabled}
								loading={isLoading}
							/>
						</div>
					</form>
				</div>
			</div>

			<Modal
				open={showConfirmation}
				onClose={handleCloseModal}
				type='fullscreen'
				sx='overflow-y-auto w-[90%] max-w-[400px]'
			>
				<div className='relative bg-white rounded-2xl max-w-sm w-full p-2'>
					<div>
						<div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100'>
							<CheckCircle
								className='size-12 text-h-primary'
								aria-hidden='true'
								weight='duotone'
							/>
						</div>
						<div className='mt-4 text-center'>
							<h3 className='text-lg font-medium text-gray-900'>
								Solicitud recibida
							</h3>
							<div className='mt-3'>
								<p className='text-sm md:text-base text-gray-500'>
									Hemos recibido tu solicitud y te hemos copiado en el correo,
									serás atendido en un plazo de{' '}
									<b>6 a 48 horas a través del correo,</b> trabajamos para
									brindarte una solución lo más pronto posible.
								</p>
							</div>
						</div>
					</div>
					<PrimaryButton
						label='Volver al inicio'
						onClick={handleCloseModal}
						sx='mt-5'
					/>
				</div>
			</Modal>
		</section>
	);
};

export default SupportFormPage;
